<script setup>
import { useTemplateRef, ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { __ } from 'JIX/gettext.js';
import DsFilterButton from 'JIX/components/ds/DsFilterButton.vue';
import DsFilterButtonRemainderCount from 'JIX/components/ds/DsFilterButtonRemainderCount.vue';
import { useStore } from 'vuex';
import { useUpdateQuery } from './composables';
import { bsTooltip as vBsTooltip } from 'JIX/vue-bs-tooltip.js';
import { computeCumulativeSums } from 'JIX/jobsearch/utils';
import { paramIconMap } from './constants';

const store = useStore();

const props = defineProps({
    hideCloseButton: {
        type: Boolean,
        default: false,
    },
    queryType: {
        type: String,
        default: 'workingQuery',
        validator: value => ['searchQuery', 'searchQueryWorking', 'workingQuery'].includes(value),
    },
    showResetSearchButton: {
        type: Boolean,
        default: false,
    },
    showResetButtonOnExpand: {
        type: Boolean,
        default: false
    },
});

const container = useTemplateRef('container');
const displayAllBadges = ref(false);

defineEmits([
    'queryParameterRemoved',
    'resetSearch',
]);

defineExpose({
    collapseBadges() {
        displayAllBadges.value = false;
    },
});

const {
    handleRemoveChosenQueryParam,
    handleRemoveChosenQueryParams: _handleRemoveChosenQueryParams,
} = useUpdateQuery(() => {}, store);


const selectedValues = computed(() => store.getters.selectedValues(props.queryType));


const badges = computed(() => Object.keys(selectedValues.value)
    .flatMap(type => selectedValues.value[type].map(queryParam => [type, queryParam]))
    .sort(([_type0, queryParam0], [_type1, queryParam1]) => {
        if (queryParam0.text.length > queryParam1.text.length) {
            return 1;
        } else if (queryParam0.text.length < queryParam1.text.length) {
            return -1;
        }
        return 0;
    })
);

const indexBadgesToDisplay = ref(badges.value.length);

async function arrangeBadges() {
    indexBadgesToDisplay.value = badges.value.length;
    await nextTick();
    let computedBadgeIndex = computeBadgeIndex();
    if (displayAllBadges.value && badges.value.length <= computedBadgeIndex) {
        //There is enough space to show all badges on one line
        displayAllBadges.value = false;
        indexBadgesToDisplay.value = computedBadgeIndex;
    } else if (!displayAllBadges.value){
        indexBadgesToDisplay.value = computedBadgeIndex;
    }

}

onMounted(() => {
    arrangeBadges();
    addEventListener("resize", arrangeBadges);
});

onUnmounted(() => {
    removeEventListener("resize", arrangeBadges);
});

watch([badges, displayAllBadges], arrangeBadges);

function computeBadgeIndex() {
    if (container.value) {
        const gapValue = 10;
        const roomForAdditionalBadgesBadge = 50;
        const availableWidthForBadges = container.value.clientWidth - roomForAdditionalBadgesBadge;

        const badgesWidth = Array.from(container.value.children).map(badge => badge.clientWidth + gapValue);
        const badgesCumulativeWidth = computeCumulativeSums(badgesWidth, -gapValue); //-gapValue as offset, as the first badge has no gap
        const firstIndexOverflow = badgesCumulativeWidth.findIndex(cumulativeWidth => cumulativeWidth > availableWidthForBadges);

        if (firstIndexOverflow > 0) { // There is not room for everything !== -1 and there is room for at least one thing, !== 0.
            return firstIndexOverflow;
        }
    }
    return badges.value.length;
}
</script>

<template>
    <div class="flex-fill" v-if="Object.keys(selectedValues).length">
        <slot name="title" />
        <div class="filter-button-list" ref="container">
            <DsFilterButton
                v-for="[type, queryParam] in badges.slice(0, indexBadgesToDisplay)"
                @click="() => { handleRemoveChosenQueryParam(queryParam, type); $emit('queryParameterRemoved') }"
                v-bs-tooltip="store.state.paramTitles[type]"
                :icon-name="paramIconMap[type]"
                :icon-label="store.state.paramTitles[type] + ':'"
                :label="queryParam.text"
                :key="queryParam.text"
            />
            <DsFilterButtonRemainderCount
                @click="displayAllBadges = true"
                :count="badges.length - indexBadgesToDisplay"
            />
        </div>
        <div class="d-flex justify-content-between">
            <button
                v-if="showResetSearchButton || (showResetButtonOnExpand && displayAllBadges)"
                class="btn btn-tertiary btn-sm mt-2"
                type="button"
                @click.prevent="$emit('resetSearch')"
            >
                {{ __('Nulstil') }}
            </button>
            <button
                class="btn btn-tertiary btn-sm mt-2"
                type="button"
                @click="displayAllBadges = false"
                v-if="displayAllBadges && !hideCloseButton"
            >
                {{ __('Luk') }}
            </button>
        </div>
    </div>
</template>
